import React, { useContext } from 'react';
import { Alert, Box, Button, Container, Header, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import { ColumnSourceType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { columnTypeLabel, periodBehaviorSummary} from 'src/components/segment-cdt/create/segmentCreateUtils';
import { submitCreate } from 'src/components/segment-cdt/create/segmentCreateActions';
import ColumnTypeChooser from 'src/components/segment-cdt/create/ColumnTypeChooser';
import GlPullColumnForm from 'src/components/segment-cdt/create/GlPullColumnForm';
import ManualColumnForm from 'src/components/segment-cdt/create/ManualColumnForm';

/**
 * Wizard Step 2: the running column list (order == value_order, fixed after create) with add / remove /
 * reorder, plus the "Create Table" submit. When a column is being added it swaps in the type chooser or
 * the GL/Manual sub-form. Reads everything from context; submit is dispatched here, not passed in.
 */
export default function SegmentColumnsStep() {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;

    if (editor) {
        return editor.draft.sourceType === ColumnSourceType.PULL_GL ? <GlPullColumnForm /> : <ManualColumnForm />;
    }

    if (cw.choosing) {
        return (
            <ColumnTypeChooser
                onPick={(sourceType) => dispatch(ACTIONS.START_COLUMN_EDITOR.withPayload({ sourceType }))}
                onCancel={() => dispatch(ACTIONS.SET_CHOOSING.withPayload(false))}
            />
        );
    }

    return (
        <SpaceBetween size="m">
            <Header variant="h3" description="Add one or more columns. Column order is set here and is fixed after creation.">
                Columns
            </Header>

            {/* The box holds the columns already added; "Add column" appends a new row into it. */}
            <Container>
                {cw.columns.length === 0 ? (
                    <Box color="text-body-secondary">No columns added yet. Use "Add column" below to add the first one.</Box>
                ) : (
                    <SpaceBetween size="xs">
                        {cw.columns.map((col, i) => (
                            <div
                                key={col.columnId}
                                style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}
                            >
                                <SpaceBetween size="s" direction="horizontal" alignItems="center">
                                    <Box variant="strong">
                                        {i + 1}. {col.displayName}
                                    </Box>
                                    <Box variant="small" color="text-body-secondary">
                                        {/* Period behaviour is shown here because columns are add-only: after
                                            saving there is no edit path, so this is the last chance to notice
                                            a mis-picked behaviour. */}
                                        {[columnTypeLabel(col.sourceType), periodBehaviorSummary(col)]
                                            .filter((part) => !!part).join(' · ')}
                                    </Box>
                                </SpaceBetween>
                                <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                                    <Button
                                        variant="icon"
                                        iconName="angle-up"
                                        ariaLabel="Move up"
                                        disabled={i === 0}
                                        onClick={() => dispatch(ACTIONS.MOVE_COLUMN.withPayload({ index: i, dir: -1 }))}
                                    />
                                    <Button
                                        variant="icon"
                                        iconName="angle-down"
                                        ariaLabel="Move down"
                                        disabled={i === cw.columns.length - 1}
                                        onClick={() => dispatch(ACTIONS.MOVE_COLUMN.withPayload({ index: i, dir: 1 }))}
                                    />
                                    <Button variant="link" onClick={() => dispatch(ACTIONS.REMOVE_COLUMN.withPayload(i))}>
                                        Remove
                                    </Button>
                                </SpaceBetween>
                            </div>
                        ))}
                    </SpaceBetween>
                )}
            </Container>

            <Button iconName="add-plus" onClick={() => dispatch(ACTIONS.SET_CHOOSING.withPayload(true))}>
                Add column
            </Button>

            {cw.createError && <Alert type="error">{cw.createError}</Alert>}

            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    <Button disabled={cw.creating} onClick={() => dispatch(ACTIONS.SET_WIZARD_STEP.withPayload(1))}>
                        Back
                    </Button>
                    <Button variant="primary" loading={cw.creating} disabled={cw.columns.length === 0} onClick={() => submitCreate(cw, services, dispatch)}>
                        Create Table
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
